import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, Status, type ComplianceDetail } from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import './ComplianceEditor.css'

/**
 * Inline editor for one obligation.
 *
 * The four states are a radio group, not a dropdown, because they are not
 * interchangeable options — choosing "Not applicable" over "Unknown" is a
 * statement about the vehicle, and burying that in a collapsed <select> makes it
 * too easy to pick the wrong one without reading.
 */
export function ComplianceEditor({
  vehicleId,
  detail,
  onDone,
}: {
  vehicleId: number
  detail: ComplianceDetail
  onDone: () => void
}) {
  const [status, setStatus] = useState<number>(detail.status ?? Status.Unknown)
  const [expiryDate, setExpiryDate] = useState(detail.expiryDate ?? '')
  const [certificateNumber, setCertificateNumber] = useState(detail.certificateNumber ?? '')
  const [providerName, setProviderName] = useState(detail.providerName ?? '')

  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: () =>
      api.editCompliance(vehicleId, detail.complianceTypeId!, {
        status: status as 1 | 2 | 3 | 4,
        // Only Known carries a date. Sending one with any other status would be
        // rejected by the database CHECK constraint anyway; stripping it here
        // means the user gets a clear form error instead of a 400 from the server.
        expiryDate: status === Status.Known ? expiryDate : null,
        certificateNumber: certificateNumber || null,
        providerName: providerName || null,
        // The RowVersion read with this record. If anyone else has saved since,
        // the server rejects this write rather than silently discarding theirs.
        rowVersion: detail.rowVersion!,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      await invalidateFleet(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['activity', vehicleId] })
      onDone()
    },
  })

  const conflict = save.error instanceof ApiError && save.error.isConflict
  const dateMissing = status === Status.Known && !expiryDate

  return (
    <form
      className="editor"
      onSubmit={(e) => {
        e.preventDefault()
        if (!dateMissing) save.mutate()
      }}
    >
      <fieldset className="editor__states">
        <legend className="eyebrow">State</legend>

        {[
          { value: Status.Known, label: 'Has an expiry date' },
          { value: Status.NoExpiry, label: 'Never expires (LTT / OTT)' },
          { value: Status.NotApplicable, label: 'Not applicable to this vehicle' },
          { value: Status.Unknown, label: "Unknown — we don't have it" },
        ].map((option) => (
          <label key={option.value} className="editor__radio">
            <input
              type="radio"
              name={`status-${detail.complianceTypeId}`}
              checked={status === option.value}
              onChange={() => setStatus(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>

      {status === Status.Known && (
        <label className="editor__field">
          <span className="eyebrow">Expires on</span>
          <input
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            required
          />
        </label>
      )}

      <label className="editor__field">
        <span className="eyebrow">Certificate / policy no.</span>
        <input
          type="text"
          className="mono"
          value={certificateNumber}
          onChange={(e) => setCertificateNumber(e.target.value)}
          placeholder="optional"
        />
      </label>

      <label className="editor__field">
        <span className="eyebrow">Provider</span>
        <input
          type="text"
          value={providerName}
          onChange={(e) => setProviderName(e.target.value)}
          placeholder="optional"
        />
      </label>

      {conflict && (
        <p className="editor__conflict" role="alert">
          <b>Someone else changed this vehicle while you were editing.</b> Close and reopen it
          to see their change, then apply yours again. Nothing you typed has been saved.
        </p>
      )}

      {save.error && !conflict && (
        <p className="editor__error" role="alert">
          {(save.error as Error).message}
        </p>
      )}

      <p className="editor__note">
        Saving marks this as hand-corrected. A later spreadsheet import or registry
        refresh will not overwrite it.
      </p>

      <div className="editor__actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending || dateMissing}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn" onClick={onDone} disabled={save.isPending}>
          Cancel
        </button>
      </div>
    </form>
  )
}
